import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Sunrise, Layers, Inbox, PlusCircle, Building2, Package, LogOut, TowerControl, Sun, Moon, FolderOpen, Tag, BadgePercent, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getTheme, applyTheme } from "../lib/theme";
import api from "../lib/api";

const NAV = [
  { to: "/", label: "Brief", icon: Sunrise, testid: "nav-daily-brief-tab", end: true },
  { to: "/events", label: "Events", icon: Layers, testid: "nav-events-feed-tab" },
  { to: "/confirm", label: "Confirm", icon: Inbox, testid: "nav-confirm-queue-tab", badge: true },
  { to: "/capture", label: "Capture", icon: PlusCircle, testid: "nav-capture-tab" },
  { to: "/vendors", label: "Brands", icon: Building2, testid: "nav-vendor-memory-tab" },
  { to: "/pricing", label: "Pricing", icon: Tag, testid: "nav-pricing-tab" },
  { to: "/schemes", label: "Schemes", icon: BadgePercent, testid: "nav-schemes-tab" },
  { to: "/search", label: "Search", icon: Search, testid: "nav-price-search-tab" },
  { to: "/catalog", label: "Catalog", icon: Package, testid: "nav-catalog-tab" },
  { to: "/library", label: "Library", icon: FolderOpen, testid: "nav-library-tab" },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmCount, setConfirmCount] = useState(0);
  const [theme, setTheme] = useState(getTheme);

  useEffect(() => {
    const sync = () => setTheme(getTheme());
    window.addEventListener("shd-theme", sync);
    return () => window.removeEventListener("shd-theme", sync);
  }, []);

  useEffect(() => {
    const load = () =>
      api
        .get("/stats/confirm-count")
        .then((r) => setConfirmCount(r.data.count))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-200 dark:bg-slate-950">
      <header className="sticky top-0 z-50 border-b border-slate-300 dark:border-slate-700 bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 shrink-0" data-testid="app-brand">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/40">
              <TowerControl className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-base font-extrabold tracking-tight text-slate-900 dark:text-slate-100 leading-none whitespace-nowrap">SHD CONTROL TOWER</div>
              <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 whitespace-nowrap">Sethi Home Decor</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 rounded-md px-3 py-2 text-base font-medium transition-colors ${
                    isActive ? "bg-slate-100 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
                {item.badge && confirmCount > 0 && (
                  <span data-testid="nav-confirm-badge" className="ml-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-sm font-mono font-bold text-amber-950">
                    {confirmCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="theme-toggle-btn"
              onClick={() => applyTheme(theme === "dark" ? "light" : "dark")}
              title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              className="flex items-center justify-center rounded-md border border-slate-300 dark:border-slate-700 p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <span className="hidden sm:block text-sm font-mono text-slate-600 dark:text-slate-400" data-testid="user-email-label">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:border-rose-400 dark:hover:border-rose-500/50 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">{children}</main>

      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-slate-300 dark:border-slate-700 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl"
      >
        <div className="flex overflow-x-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `relative flex flex-1 min-w-[60px] flex-col items-center gap-1 py-2.5 text-[10px] font-mono uppercase tracking-tight transition-colors ${
                  isActive ? "text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              {item.badge && confirmCount > 0 && (
                <span className="absolute top-1 right-1/2 translate-x-4 rounded-full bg-amber-500 px-1 text-[9px] font-mono font-bold text-amber-950">
                  {confirmCount}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
